import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, MapPin, ShieldCheck, Award, Phone, Mail, ExternalLink, Filter, X, Users, ThumbsUp, Bookmark, BookmarkCheck, StickyNote, Trash2, Compass, Inbox, GitCompareArrows, Check, Clock, Building2, Printer, HeartHandshake } from "lucide-react";
import { useApp } from "@/context/AppContext";
import {
  searchWorkers, getCategories, endorseSkill, getShortlist, addShortlist, updateShortlistNote, removeShortlist,
  listEndorsementRequests, fulfillEndorsementRequest, declineEndorsementRequest, listCommunities,
} from "@/lib/api";
import { Header, SkillTag, CredentialBadge, categoryIcon } from "@/components/shared";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";

const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const printCompare = (workers, employer) => {
  if (workers.length < 2) return;
  const [a, b] = workers;
  const verified = (w) => w.skills.filter((s) => s.verified_status === "verified");
  const allNames = Array.from(new Set([...verified(a), ...verified(b)].map((s) => s.skill_name)));
  const sf = (w, name) => verified(w).find((s) => s.skill_name === name);
  const dots = (n) => Array.from({ length: 5 }, (_, i) => `<span class="d ${i < n ? "on" : ""}"></span>`).join("");
  const skillCell = (s) => s
    ? `<div class="skill"><b>${escapeHtml(s.skill_name)}</b><div class="dots">${dots(s.proficiency_level)}</div>${(s.endorsement_count || 0) > 0 ? `<div class="end">👍 ${s.endorsement_count} endorsed</div>` : ""}</div>`
    : `<span class="muted">—</span>`;
  const badgeCell = (w) => w.badges.length
    ? w.badges.map((bd) => `<div class="badge"><b>${escapeHtml(bd.badge_title)}</b><div class="muted">#${escapeHtml(bd.public_verify_hash)}</div></div>`).join("")
    : `<span class="muted">—</span>`;
  const workerHeader = (w) => `
    <div class="whead">
      <div class="wname">${escapeHtml(w.name)}</div>
      <div class="muted">${escapeHtml(w.headline || "")}</div>
      <div class="muted">${escapeHtml(w.location || "")}</div>
      <div class="muted">${escapeHtml(w.phone || "")} · ${escapeHtml(w.email || "")}</div>
    </div>`;
  const rows = allNames.map((n) => `
    <tr><td class="lbl">${escapeHtml(n)}</td><td>${skillCell(sf(a, n))}</td><td>${skillCell(sf(b, n))}</td></tr>
  `).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>SkillBridge · Comparison · ${escapeHtml(a.name)} vs ${escapeHtml(b.name)}</title>
    <style>
      *{box-sizing:border-box}
      body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#0f172a;padding:32px;max-width:960px;margin:0 auto}
      .brand{display:flex;align-items:center;gap:10px;color:#1d4ed8;font-weight:800;font-size:14px;text-transform:uppercase;letter-spacing:.2em}
      h1{font-size:28px;margin:8px 0 4px;letter-spacing:-.5px}
      .sub{color:#64748b;margin-bottom:20px;font-size:13px}
      table{width:100%;border-collapse:collapse;margin-top:14px;font-size:13px}
      th,td{padding:12px;border-top:1px solid #e2e8f0;vertical-align:top;text-align:left}
      th{background:#f1f5f9;font-size:11px;text-transform:uppercase;letter-spacing:.15em;color:#475569}
      .lbl{font-weight:700;color:#334155;width:22%}
      .muted{color:#64748b;font-size:12px}
      .whead{display:flex;flex-direction:column;gap:2px}
      .wname{font-weight:800;font-size:18px}
      .skill b{display:block;margin-bottom:4px}
      .dots{display:inline-flex;gap:3px;margin-right:6px}
      .d{width:6px;height:6px;border-radius:50%;background:#e2e8f0;display:inline-block}
      .d.on{background:#1d4ed8}
      .end{margin-top:4px;color:#1d4ed8;font-size:11px;font-weight:700}
      .badge{border:1px solid #e2e8f0;border-radius:10px;padding:8px 10px;margin-bottom:6px}
      .foot{margin-top:24px;padding-top:16px;border-top:1px solid #e2e8f0;color:#94a3b8;font-size:11px;display:flex;justify-content:space-between}
      @media print{body{padding:20px} .noprint{display:none}}
    </style></head><body>
    <div class="brand">🛡 SkillBridge · Verified Talent Comparison</div>
    <h1>${escapeHtml(a.name)} <span class="muted">vs</span> ${escapeHtml(b.name)}</h1>
    <div class="sub">Prepared${employer?.company ? ` for <b>${escapeHtml(employer.company)}</b>` : ""} on ${new Date().toLocaleDateString()}</div>
    <table>
      <thead><tr><th></th><th>${escapeHtml(a.name)}</th><th>${escapeHtml(b.name)}</th></tr></thead>
      <tbody>
        <tr><td class="lbl">Profile</td><td>${workerHeader(a)}</td><td>${workerHeader(b)}</td></tr>
        <tr><td class="lbl">Verified skills</td><td><b>${verified(a).length}</b></td><td><b>${verified(b).length}</b></td></tr>
        <tr><td class="lbl">Credential badges</td><td>${badgeCell(a)}</td><td>${badgeCell(b)}</td></tr>
        <tr><td class="lbl">Total endorsements</td><td><b>${a.endorsement_total || 0}</b></td><td><b>${b.endorsement_total || 0}</b></td></tr>
      </tbody>
    </table>
    <table>
      <thead><tr><th class="lbl">Skill</th><th>${escapeHtml(a.name)}</th><th>${escapeHtml(b.name)}</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="foot"><span>Credentials issued & verified by SkillBridge</span><span>skillbridge.app</span></div>
    <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
    </body></html>`;
  const win = window.open("", "_blank", "width=900,height=1000");
  if (!win) { alert("Please allow pop-ups to export the comparison"); return; }
  win.document.open();
  win.document.write(html);
  win.document.close();
};

const WorkerCard = ({ worker, onOpen, index, saved, onToggleSave }) => {
  const verified = worker.skills.filter((s) => s.verified_status === "verified");
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      data-testid={`worker-card-${worker.id}`}
      className="relative rounded-lg border border-border bg-card p-4 hover:border-slate-400 dark:hover:border-slate-600 hover:shadow-sm transition-all"
    >
      <button
        data-testid={`save-toggle-${worker.id}`}
        onClick={(e) => { e.stopPropagation(); onToggleSave(worker); }}
        aria-label={saved ? "Remove from shortlist" : "Add to shortlist"}
        className={`absolute top-3 right-3 w-8 h-8 rounded-md flex items-center justify-center transition-colors ${saved ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-primary"}`}
      >
        {saved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
      </button>
      <button onClick={() => onOpen(worker)} className="w-full text-left">
        <div className="flex items-center gap-3 pr-8">
          <Avatar className="w-12 h-12 border border-border">
            <AvatarImage src={worker.avatar_url} className="object-cover" />
            <AvatarFallback>{worker.name[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-sm truncate">{worker.name}</div>
            <div className="text-xs text-muted-foreground truncate">{worker.headline}</div>
            <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{worker.location}</div>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 text-[11px] font-semibold flex-wrap">
          <span className="inline-flex items-center gap-1 text-primary"><ShieldCheck className="w-3 h-3" />{verified.length} verified</span>
          <span className="inline-flex items-center gap-1 text-primary"><Award className="w-3 h-3" />{worker.badges.length} badges</span>
          {worker.endorsement_total > 0 && (
            <span className="inline-flex items-center gap-1 text-primary"><ThumbsUp className="w-3 h-3" />{worker.endorsement_total} endorsed</span>
          )}
        </div>
        <div className="mt-2.5 flex flex-wrap gap-1">
          {verified.slice(0, 3).map((s) => (
            <span key={s.id} className="text-[11px] rounded border border-border bg-secondary/60 px-2 py-0.5 font-medium">{s.skill_name}</span>
          ))}
          {verified.length > 3 && <span className="text-[11px] text-muted-foreground px-1">+{verified.length - 3}</span>}
        </div>
      </button>
    </motion.div>
  );
};

export default function EmployerDashboard() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const employerId = currentUser.id;
  const [workers, setWorkers] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState(ALL);
  const [minProf, setMinProf] = useState(ALL);
  const [location, setLocation] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("discover");
  const [shortlist, setShortlist] = useState([]);
  const [requests, setRequests] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [communities, setCommunities] = useState([]);
  const [community, setCommunity] = useState(ALL);

  useEffect(() => { listCommunities().then(setCommunities).catch(() => {}); }, []);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "requests" || t === "shortlist" || t === "discover") {
      setView(t);
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { getCategories().then(setCategories); }, []);

  const loadShortlist = useCallback(() => {
    getShortlist(employerId).then(setShortlist);
  }, [employerId]);
  const loadRequests = useCallback(() => {
    listEndorsementRequests({ employer_id: employerId, status: "pending" }).then(setRequests);
  }, [employerId]);
  useEffect(() => { loadShortlist(); loadRequests(); }, [loadShortlist, loadRequests]);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = {};
      if (q) params.skill = q;
      if (category !== ALL) params.category = category;
      if (minProf !== ALL) params.min_proficiency = Number(minProf);
      if (location) params.location = location;
      if (community !== ALL) params.community = community;
      searchWorkers(params).then(setWorkers);
    }, 300);
    return () => clearTimeout(t);
  }, [q, category, minProf, location, community]);

  const clear = () => { setQ(""); setCategory(ALL); setMinProf(ALL); setLocation(""); setCommunity(ALL); };
  const hasFilters = q || category !== ALL || minProf !== ALL || location || community !== ALL;
  const savedIds = new Set(shortlist.map((e) => e.worker_id));

  const toggleSave = async (worker) => {
    if (savedIds.has(worker.id)) {
      await removeShortlist(employerId, worker.id);
      toast.success(`${worker.name} removed from shortlist`);
      setCompareIds((ids) => ids.filter((id) => id !== worker.id));
    } else {
      await addShortlist(employerId, worker.id, "");
      toast.success(`${worker.name} added to shortlist`);
    }
    loadShortlist();
  };

  const applyEndorsement = (skillId, res) => {
    const patch = (w) => ({
      ...w,
      skills: w.skills.map((s) =>
        s.id === skillId
          ? {
              ...s,
              endorsement_count: res.endorsement_count,
              endorsed_by: res.endorsed
                ? [...(s.endorsed_by || []), employerId]
                : (s.endorsed_by || []).filter((id) => id !== employerId),
            }
          : s
      ),
    });
    setSelected((prev) => (prev ? { ...patch(prev), ...{ endorsement_total: patch(prev).skills.reduce((a, s) => a + s.endorsement_count, 0) } } : prev));
    setWorkers((prev) => prev && prev.map((w) => (w.id === selected?.id ? { ...patch(w), endorsement_total: patch(w).skills.reduce((a, s) => a + s.endorsement_count, 0) } : w)));
  };

  const endorse = async (skill) => {
    const res = await endorseSkill(skill.id, employerId);
    applyEndorsement(skill.id, res);
    toast.success(res.endorsed ? "Skill endorsed" : "Endorsement removed");
  };

  const toggleCompare = (workerId) => {
    setCompareIds((ids) => {
      if (ids.includes(workerId)) return ids.filter((id) => id !== workerId);
      if (ids.length >= 2) {
        toast.info("Only 2 workers can be compared. Deselect one to swap.");
        return ids;
      }
      return [...ids, workerId];
    });
  };

  return (
    <div className="min-h-screen grain">
      <Header title="Employer" right={<span className="hidden sm:inline text-sm text-muted-foreground">{currentUser.company}</span>} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <Users className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Talent Discovery</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Find verified talent</h1>
        <p className="mt-2 text-foreground/80 max-w-lg">Search proven, verified workers. Endorse their skills, respond to requests, and compare shortlisted workers side by side.</p>

        {/* View tabs */}
        <div className="mt-6 inline-flex rounded-full border border-border bg-card p-1 flex-wrap">
          <button
            data-testid="tab-discover"
            onClick={() => setView("discover")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${view === "discover" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >
            <Compass className="w-4 h-4" /> Discover
          </button>
          <button
            data-testid="tab-shortlist"
            onClick={() => setView("shortlist")}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${view === "shortlist" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >
            <Bookmark className="w-4 h-4" /> Shortlist ({shortlist.length})
          </button>
          <button
            data-testid="tab-requests"
            onClick={() => setView("requests")}
            className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${view === "requests" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
          >
            <Inbox className="w-4 h-4" /> Requests
            {requests.length > 0 && (
              <span data-testid="requests-count-pill" className={`ml-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${view === "requests" ? "bg-white/20" : "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200"}`}>
                {requests.length}
              </span>
            )}
          </button>
        </div>

        {view === "discover" && (
          <>
            {/* Filters */}
            <div className="mt-6 rounded-lg border border-border bg-card p-4 sm:p-5">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="relative sm:col-span-2 lg:col-span-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="filter-skill" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Skill e.g. Wood Joinery" className="pl-9" />
                </div>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={minProf} onValueChange={setMinProf}>
                  <SelectTrigger data-testid="filter-proficiency"><SelectValue placeholder="Min proficiency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Any proficiency</SelectItem>
                    {[2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}+ / 5</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input data-testid="filter-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="pl-9" />
                </div>
                <Select value={community} onValueChange={setCommunity}>
                  <SelectTrigger data-testid="filter-community"><SelectValue placeholder="Community" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All communities</SelectItem>
                    {communities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {hasFilters && (
                <button data-testid="clear-filters-btn" onClick={clear} className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" /> Clear filters
                </button>
              )}
            </div>

            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              {workers === null ? "Searching…" : `${workers.length} worker${workers.length === 1 ? "" : "s"} found`}
            </div>

            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {workers === null
                ? [0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-52 rounded-lg" />)
                : (
                  <AnimatePresence mode="popLayout">
                    {workers.map((w, i) => (
                      <WorkerCard key={w.id} worker={w} onOpen={setSelected} index={i} saved={savedIds.has(w.id)} onToggleSave={toggleSave} />
                    ))}
                  </AnimatePresence>
                )}
            </div>
            {workers && workers.length === 0 && (
              <div className="mt-10 text-center text-muted-foreground">No workers match these filters. Try broadening your search.</div>
            )}
          </>
        )}

        {view === "shortlist" && (
          <ShortlistView
            shortlist={shortlist}
            employerId={employerId}
            onOpen={setSelected}
            reload={loadShortlist}
            compareIds={compareIds}
            onToggleCompare={toggleCompare}
            onOpenCompare={() => setShowCompare(true)}
          />
        )}

        {view === "requests" && (
          <RequestsView
            requests={requests}
            onOpen={setSelected}
            reload={() => { loadRequests(); }}
          />
        )}
      </div>

      {/* Profile modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="worker-profile-modal">
          <DialogTitle className="sr-only">{selected?.name} profile</DialogTitle>
          <DialogDescription className="sr-only">Verified credentials, endorsements and contact options for {selected?.name}</DialogDescription>
          {selected && (
            <ProfileModalContent
              worker={selected}
              navigate={navigate}
              employerId={employerId}
              onEndorse={endorse}
              saved={savedIds.has(selected.id)}
              onToggleSave={() => toggleSave(selected)}
              note={shortlist.find((e) => e.worker_id === selected.id)?.note || ""}
              onReloadShortlist={loadShortlist}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Compare modal */}
      <Dialog open={showCompare} onOpenChange={setShowCompare}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="compare-modal">
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <GitCompareArrows className="w-6 h-6 text-primary" /> Side-by-side comparison
          </DialogTitle>
          <DialogDescription>Compare skills, credentials and endorsements between two shortlisted workers.</DialogDescription>
          {(() => {
            const cmpWorkers = compareIds.map((id) => shortlist.find((e) => e.worker_id === id)?.worker).filter(Boolean);
            return (
              <>
                <div className="flex justify-end -mt-2">
                  <Button
                    data-testid="compare-print-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => printCompare(cmpWorkers, currentUser)}
                    disabled={cmpWorkers.length < 2}
                  >
                    <Printer className="w-4 h-4 mr-1.5" /> Print / Save PDF
                  </Button>
                </div>
                <CompareContent workers={cmpWorkers} navigate={navigate} />
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ShortlistView = ({ shortlist, employerId, onOpen, reload, compareIds, onToggleCompare, onOpenCompare }) => {
  if (shortlist.length === 0) {
    return (
      <div className="mt-10 text-center">
        <Bookmark className="w-10 h-10 mx-auto text-muted-foreground" />
        <p className="mt-3 text-muted-foreground">Your shortlist is empty. Save workers from Discover to compare them here.</p>
      </div>
    );
  }
  const canCompare = compareIds.length === 2;
  return (
    <div className="mt-6">
      <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 text-sm">
          <GitCompareArrows className="w-4 h-4 text-primary" />
          <span className="font-semibold">Compare mode</span>
          <span className="text-muted-foreground" data-testid="compare-selection-count">
            {compareIds.length}/2 selected
          </span>
        </div>
        <Button
          data-testid="open-compare-btn"
          size="sm"
          onClick={onOpenCompare}
          disabled={!canCompare}
        >
          <GitCompareArrows className="w-4 h-4 mr-1.5" />
          Compare {canCompare ? "workers" : "(select 2)"}
        </Button>
      </div>

      <div className="mt-4 space-y-4">
        {shortlist.map((entry) => (
          <ShortlistRow
            key={entry.id}
            entry={entry}
            employerId={employerId}
            onOpen={onOpen}
            reload={reload}
            compareChecked={compareIds.includes(entry.worker_id)}
            onToggleCompare={() => onToggleCompare(entry.worker_id)}
          />
        ))}
      </div>
    </div>
  );
};

const ShortlistRow = ({ entry, employerId, onOpen, reload, compareChecked, onToggleCompare }) => {
  const w = entry.worker;
  const [note, setNote] = useState(entry.note || "");
  const [saving, setSaving] = useState(false);
  const verified = w.skills.filter((s) => s.verified_status === "verified");

  const saveNote = async () => {
    setSaving(true);
    await updateShortlistNote(employerId, w.id, note);
    setSaving(false);
    toast.success("Note saved");
  };
  const remove = async () => {
    await removeShortlist(employerId, w.id);
    toast.success(`${w.name} removed`);
    reload();
  };

  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`rounded-lg border bg-card p-5 transition-colors ${compareChecked ? "border-primary" : "border-border"}`} data-testid={`shortlist-row-${w.id}`}>
      <div className="flex items-start gap-3">
        <button
          data-testid={`compare-check-${w.id}`}
          onClick={onToggleCompare}
          aria-label={compareChecked ? "Remove from comparison" : "Add to comparison"}
          className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${compareChecked ? "bg-primary border-primary text-primary-foreground" : "border-border hover:border-primary"}`}
        >
          {compareChecked && <Check className="w-4 h-4" />}
        </button>
        <Avatar className="w-12 h-12 border border-border">
          <AvatarImage src={w.avatar_url} className="object-cover" />
          <AvatarFallback>{w.name[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <button onClick={() => onOpen(w)} className="font-heading font-bold hover:text-primary transition-colors">{w.name}</button>
          <div className="text-sm text-muted-foreground truncate">{w.headline} · {w.location}</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {verified.slice(0, 4).map((s) => (
              <span key={s.id} className="text-xs rounded-full bg-secondary px-2 py-0.5 font-medium">{s.skill_name}</span>
            ))}
          </div>
        </div>
        <button data-testid={`shortlist-remove-${w.id}`} onClick={remove} aria-label="Remove" className="w-9 h-9 rounded-full bg-secondary text-muted-foreground hover:text-destructive flex items-center justify-center transition-colors shrink-0">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4">
        <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground flex items-center gap-1.5 mb-2"><StickyNote className="w-3.5 h-3.5" /> Private note</label>
        <Textarea
          data-testid={`shortlist-note-${w.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Great for the Lagos site project, call next week…"
          className="resize-none"
          rows={2}
        />
        <div className="mt-2 flex justify-end">
          <Button data-testid={`shortlist-save-note-${w.id}`} size="sm" onClick={saveNote} disabled={saving || note === (entry.note || "")}>
            Save note
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const RequestsView = ({ requests, onOpen, reload }) => {
  const [busy, setBusy] = useState(null);

  const fulfill = async (r) => {
    setBusy(r.id);
    try {
      await fulfillEndorsementRequest(r.id);
      toast.success(`${r.skill_name} endorsed for ${r.worker?.name || "worker"}`);
      reload();
    } catch {
      toast.error("Could not fulfill request");
    } finally {
      setBusy(null);
    }
  };
  const decline = async (r) => {
    setBusy(r.id);
    try {
      await declineEndorsementRequest(r.id);
      toast.info("Request declined");
      reload();
    } catch {
      toast.error("Could not decline");
    } finally {
      setBusy(null);
    }
  };

  if (requests.length === 0) {
    return (
      <div className="mt-10 text-center">
        <Inbox className="w-10 h-10 mx-auto text-muted-foreground" />
        <p className="mt-3 text-muted-foreground">No pending endorsement requests. Workers you've hired will show up here when they ask.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 space-y-3">
      {requests.map((r) => (
        <motion.div
          key={r.id}
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid={`request-row-${r.id}`}
          className="rounded-lg border border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
        >
          <button onClick={() => r.worker && onOpen(r.worker)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
            <Avatar className="w-12 h-12 border border-border">
              <AvatarImage src={r.worker?.avatar_url} className="object-cover" />
              <AvatarFallback>{r.worker?.name?.[0] || "?"}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-heading font-bold truncate">{r.worker?.name}</div>
              <div className="text-sm text-muted-foreground truncate">
                asks you to endorse <span className="font-semibold text-foreground">{r.skill_name}</span>
              </div>
              <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> Pending</div>
            </div>
          </button>
          <div className="flex gap-2 shrink-0">
            <Button
              data-testid={`decline-request-${r.id}`}
              variant="outline"
              size="sm"
              disabled={busy === r.id}
              onClick={() => decline(r)}
            >
              Decline
            </Button>
            <Button
              data-testid={`fulfill-request-${r.id}`}
              size="sm"
              disabled={busy === r.id}
              onClick={() => fulfill(r)}
            >
              <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Endorse
            </Button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

const CompareCell = ({ children }) => <div className="p-3 sm:p-4">{children}</div>;

const CompareContent = ({ workers, navigate }) => {
  if (workers.length < 2) {
    return <div className="mt-6 text-center text-muted-foreground py-8">Select two workers to compare.</div>;
  }
  const [a, b] = workers;
  const verifiedA = a.skills.filter((s) => s.verified_status === "verified");
  const verifiedB = b.skills.filter((s) => s.verified_status === "verified");
  const allSkillNames = Array.from(
    new Set([...verifiedA, ...verifiedB].map((s) => s.skill_name))
  );

  const skillFor = (w, name) => w.skills.find((s) => s.skill_name === name && s.verified_status === "verified");

  const Header2 = ({ w, testid }) => (
    <div data-testid={testid} className="flex flex-col items-center text-center gap-2">
      <Avatar className="w-16 h-16 border-2 border-primary/20">
        <AvatarImage src={w.avatar_url} className="object-cover" />
        <AvatarFallback>{w.name[0]}</AvatarFallback>
      </Avatar>
      <div className="font-heading font-bold text-lg leading-tight">{w.name}</div>
      <div className="text-xs text-muted-foreground">{w.headline}</div>
      <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{w.location}</div>
      <Button size="sm" variant="outline" onClick={() => navigate(`/resume/${w.id}`)} data-testid={`compare-view-resume-${w.id}`}>
        <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Full resume
      </Button>
    </div>
  );

  const StatRow = ({ label, va, vb }) => (
    <div className="grid grid-cols-[1fr_2fr_2fr] items-center border-t border-border">
      <CompareCell><span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">{label}</span></CompareCell>
      <CompareCell><span className="font-semibold text-foreground">{va}</span></CompareCell>
      <CompareCell><span className="font-semibold text-foreground">{vb}</span></CompareCell>
    </div>
  );

  return (
    <div className="mt-4 rounded-lg border border-border overflow-hidden">
      <div className="grid grid-cols-[1fr_2fr_2fr] bg-accent">
        <CompareCell />
        <CompareCell><Header2 w={a} testid={`compare-header-${a.id}`} /></CompareCell>
        <CompareCell><Header2 w={b} testid={`compare-header-${b.id}`} /></CompareCell>
      </div>
      <StatRow label="Verified skills" va={verifiedA.length} vb={verifiedB.length} />
      <StatRow label="Badges" va={a.badges.length} vb={b.badges.length} />
      <StatRow label="Total endorsements" va={a.endorsement_total || 0} vb={b.endorsement_total || 0} />

      <div className="grid grid-cols-[1fr_2fr_2fr] items-start border-t border-border">
        <CompareCell><span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">Badges</span></CompareCell>
        <CompareCell>
          <div className="flex flex-wrap gap-3">
            {a.badges.length ? a.badges.map((b2, i) => <CredentialBadge key={b2.id} badge={b2} index={i} />) : <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </CompareCell>
        <CompareCell>
          <div className="flex flex-wrap gap-3">
            {b.badges.length ? b.badges.map((b2, i) => <CredentialBadge key={b2.id} badge={b2} index={i} />) : <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </CompareCell>
      </div>

      <div className="grid grid-cols-[1fr_2fr_2fr] border-t border-border">
        <CompareCell><span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">Skills</span></CompareCell>
        <CompareCell><div className="text-xs text-muted-foreground">{a.name}</div></CompareCell>
        <CompareCell><div className="text-xs text-muted-foreground">{b.name}</div></CompareCell>
      </div>
      {allSkillNames.map((name) => {
        const sa = skillFor(a, name);
        const sb = skillFor(b, name);
        return (
          <div key={name} data-testid={`compare-skill-row-${name}`} className="grid grid-cols-[1fr_2fr_2fr] border-t border-border items-center">
            <CompareCell><span className="text-sm font-semibold">{name}</span></CompareCell>
            <CompareCell>{sa ? <SkillTag skill={sa} testid={`compare-a-${sa.id}`} /> : <span className="text-sm text-muted-foreground">—</span>}</CompareCell>
            <CompareCell>{sb ? <SkillTag skill={sb} testid={`compare-b-${sb.id}`} /> : <span className="text-sm text-muted-foreground">—</span>}</CompareCell>
          </div>
        );
      })}

      <div className="grid grid-cols-[1fr_2fr_2fr] border-t border-border">
        <CompareCell><span className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground">Contact</span></CompareCell>
        <CompareCell>
          <div className="flex gap-2">
            <a href={`tel:${a.phone}`} className="text-sm inline-flex items-center gap-1.5 text-primary"><Phone className="w-3.5 h-3.5" /> Call</a>
            <a href={`mailto:${a.email}`} className="text-sm inline-flex items-center gap-1.5 text-primary"><Mail className="w-3.5 h-3.5" /> Email</a>
          </div>
        </CompareCell>
        <CompareCell>
          <div className="flex gap-2">
            <a href={`tel:${b.phone}`} className="text-sm inline-flex items-center gap-1.5 text-primary"><Phone className="w-3.5 h-3.5" /> Call</a>
            <a href={`mailto:${b.email}`} className="text-sm inline-flex items-center gap-1.5 text-primary"><Mail className="w-3.5 h-3.5" /> Email</a>
          </div>
        </CompareCell>
      </div>
    </div>
  );
};

const ProfileModalContent = ({ worker, navigate, employerId, onEndorse, saved, onToggleSave, note, onReloadShortlist }) => {
  const verified = worker.skills.filter((s) => s.verified_status === "verified");
  const [noteDraft, setNoteDraft] = useState(note);
  useEffect(() => { setNoteDraft(note); }, [note, worker.id]);

  const saveNote = async () => {
    if (!saved) { await addShortlist(employerId, worker.id, noteDraft); }
    else { await updateShortlistNote(employerId, worker.id, noteDraft); }
    onReloadShortlist();
    toast.success("Note saved to shortlist");
  };

  return (
    <div>
      <div className="bg-primary text-primary-foreground p-6">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border-2 border-white/30">
            <AvatarImage src={worker.avatar_url} className="object-cover" />
            <AvatarFallback>{worker.name[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold tracking-tight">{worker.name}</h2>
            <p className="text-sm text-primary-foreground/85">{worker.headline}</p>
            <p className="text-xs text-primary-foreground/70 inline-flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{worker.location}</p>
          </div>
          <button
            data-testid="modal-save-toggle"
            onClick={onToggleSave}
            aria-label={saved ? "Remove from shortlist" : "Add to shortlist"}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors shrink-0"
          >
            {saved ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {worker.badges.length > 0 && (
          <div>
            <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Credentials</h3>
            <div className="flex flex-wrap gap-5 justify-center">
              {worker.badges.map((b, i) => <CredentialBadge key={b.id} badge={b} index={i} />)}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-3">Verified Skills · tap to endorse</h3>
          <div className="space-y-2">
            {verified.map((s) => {
              const mine = (s.endorsed_by || []).includes(employerId);
              return (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded-xl border border-border p-2 pl-3">
                  <SkillTag skill={s} testid={`modal-skill-${s.id}`} />
                  <Button
                    data-testid={`endorse-btn-${s.id}`}
                    size="sm"
                    variant={mine ? "default" : "outline"}
                    onClick={() => onEndorse(s)}
                    className="shrink-0"
                  >
                    <ThumbsUp className="w-3.5 h-3.5 mr-1.5" />{mine ? "Endorsed" : "Endorse"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-1.5 mb-2"><StickyNote className="w-3.5 h-3.5" /> Private note</label>
          <Textarea data-testid="modal-note" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Add a private note for your team…" rows={2} className="resize-none" />
          <div className="mt-2 flex justify-end">
            <Button data-testid="modal-save-note-btn" size="sm" variant="secondary" onClick={saveNote}>Save note</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a data-testid="contact-phone" href={`tel:${worker.phone}`} className="rounded-xl border border-border p-3 flex items-center gap-2 text-sm font-medium hover:border-primary transition-colors">
            <Phone className="w-4 h-4 text-primary" /> Call
          </a>
          <a data-testid="contact-email" href={`mailto:${worker.email}`} className="rounded-xl border border-border p-3 flex items-center gap-2 text-sm font-medium hover:border-primary transition-colors">
            <Mail className="w-4 h-4 text-primary" /> Email
          </a>
        </div>

        <Button data-testid="view-full-resume-btn" className="w-full" onClick={() => navigate(`/resume/${worker.id}`)}>
          <ExternalLink className="w-4 h-4 mr-2" /> View Full Resume
        </Button>
      </div>
    </div>
  );
};
